import { useCallback, useEffect, useState } from "react";

/**
 * Public-safe, opt-in demo preview toggle. Activated by the URL query
 * `?preview=demo`. Unlike `useDemoModeAllowed` (which is superadmin-only
 * QA gating), this hook lets a normal visitor explicitly choose to enter
 * the simulated second-screen experience after clicking an explicit
 * "View interactive demo" CTA. The synthetic match remains clearly
 * labelled as DEMO / REPLAY while this is active.
 *
 * Keeps superadmin demo gating untouched — this is a separate intent.
 */
export function usePublicDemoPreview(): { active: boolean; exit: () => void } {
  const readActive = () => {
    if (typeof window === "undefined") return false;
    try {
      return new URLSearchParams(window.location.search).get("preview") === "demo";
    } catch {
      return false;
    }
  };

  const [active, setActive] = useState<boolean>(readActive);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const recheck = () => setActive(readActive());
    window.addEventListener("popstate", recheck);
    return () => window.removeEventListener("popstate", recheck);
  }, []);

  const exit = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("preview");
      window.history.replaceState({}, "", url.toString());
      setActive(false);
    } catch {
      /* noop */
    }
  }, []);

  return { active, exit };
}